import { APIDetails } from './apis-details';

export const taxApi: APIDetails = {
  id: 'tax-ai-automation-api',
  title: 'Tax AI & Automation API',
  subtitle: 'AI-powered automated tax calculation for accurate compliance.',
  description: [
    'Handling country-specific tax rules and calculations is time-consuming, complex, and highly prone to error. Manual processes slow down operations, while traditional systems struggle to keep up with constantly changing regulations.',
    'The Tax AI & Automation API simplifies tax compliance by automating the entire process using LLM-based reasoning. It helps businesses apply the correct rules for each country and tax year, and ensure compliance with confidence. Updates to regulations are handled seamlessly, reducing risk and operational overhead.',
    'Designed for scale and reliability, the API enables faster processing, fewer errors, and smoother workflows—making it an ideal solution for tax software providers, fintech companies, and enterprise accounting teams that want to modernize tax management and focus on growth rather than complexity.',
  ],
  overview: [
    'The API provides endpoints to:',
    '• Retrieve supported countries and tax years',
    '• Fetch structured tax form schemas (JSON-based)',
    '• Create, update, retry, and delete tax calculations',
    '• Generate detailed tax breakdowns and recommendations',
    '• Integrate tax intelligence into existing systems',
  ],
  features: [
    {
      title: 'Tax Calculation Engine',
      description:
        'Compute accurate tax calculations automatically using LLM-based reasoning.',
    },
    {
      title: 'Operations',
      description:
        'Create, update, delete, and retrieve tax forms and calculations.',
    },
    {
      title: 'Retry & Error Handling',
      description: 'Automatically retry failed calculations.',
    },
    {
      title: 'Schema Retrieval',
      description: 'Access full JSON schema for tax forms.',
    },
    {
      title: 'Supported Countries & Years',
      description:
        'Get a list of countries and tax years supported for form generation.',
    },
  ],
  workflow: [
    'Get Tax Form → Get AI-driven forms for a specific country and tax year.',
    'Submit Form Data → System calculates taxes using LLM-based analysis.',
    'Update / Retry Calculations → Modify or retry failed computations.',
    'Retrieve Results → Get detailed calculation breakdowns with recommendations.',
  ],
  endpoints: [
    {
      category: 'API Endpoints',
      items: [
        {
          category: 'Get Supported Countries',
          method: 'GET',
          path: '/supported_countries',
          description: 'Returns list of supported countries for tax forms.',
        },
        {
          category: 'Get Supported Years',
          method: 'GET',
          path: '/supported_years',
          description: 'Returns supported tax years for form generation.',
        },
        {
          category: 'Get Form Schema',
          method: 'GET',
          path: '/tax_form_schema',
          description:
            'Retrieves JSON schema of tax forms with field definitions.',
        },
        {
          category: 'Create Tax Calculation',
          method: 'POST',
          path: '/tax_calculation',
          description: 'Computes tax based on provided form data.',
        },
        {
          category: 'Update Tax Calculation',
          method: 'PUT',
          path: '/tax_calculation/{tax_id}',
          description: 'Updates existing calculation and recomputes results.',
        },
        {
          category: 'Retry Failed Calculation',
          method: 'POST',
          path: '/tax_calculation/retry',
          description: 'Retries failed calculation with the same data.',
        },
        {
          category: 'Remove Tax Calculation',
          method: 'DELETE',
          path: '/tax_calculation/{tax_id}',
          description: 'Deletes a tax calculation record permanently.',
        },
        {
          category: 'Get Calculation Details',
          method: 'GET',
          path: '/tax_calculation/{tax_id}',
          description:
            'Retrieves calculation results with detailed breakdowns.',
        },
        {
          category: 'List All Calculations',
          method: 'GET',
          path: '/tax_calculations',
          description: 'Retrieves list of all calculations.',
        },
      ],
    },
  ],
  apiDocumentation: [
    {
      title: 'Supported Countries',
      method: 'GET',
      path: '/api/v1/tax/supported_countries',
      description: '6. Supported Countries',
      responseExample: ['bangladesh', 'uk'],
      statusCode: '200',
    },
    {
      title: 'Supported Years by Country',
      method: 'GET',
      path: '/api/v1/tax/supported_years',
      description: '7. Supported Years by Country',
      requestExample: {
        country: 'bangladesh',
      },
      responseExample: [2024],
      statusCode: '200',
    },
    {
      title: 'Get Tax Form Schema',
      method: 'GET',
      path: '/api/v1/tax/tax_form_schema',
      description: '8. Get Tax Form Schema',
      requestExample: {
        country: 'bangladesh',
        year: 2024,
      },
      responseExample: [
        {
          index: 1,
          fields: [
            {
              name: 'assessment_year',
              type: 'select',
              label: 'Assessment Year',
              options: [
                '2024-25 (Income Year 2023-24)',
                '2025-26 (Income Year 2024-25) or later proposed',
              ],
              required: false,
              description:
                'The assessment year determines which tax slab rates and thresholds apply (e.g., AY 2024-25 or proposed for AY 2025-26/26-27).',
              placeholder: 'Select the relevant assessment year',
            },
            {
              name: 'residency_status',
              type: 'select',
              label: 'Residency Status',
              options: [
                'Resident',
                'Non-Resident (Bangladeshi Citizen)',
                'Non-Resident (Foreign Citizen)',
              ],
              required: false,
              description:
                'Determines taxability of worldwide vs. Bangladesh-sourced income and applicable rates (e.g., flat 30% for non-resident foreign citizens).',
              placeholder: 'Select your residency status',
            },
            {
              name: 'gender',
              type: 'select',
              label: 'Gender',
              options: ['Male', 'Female', 'Third Gender'],
              required: false,
              description:
                'Women and third gender individuals may qualify for higher tax-free thresholds.',
              placeholder: 'Select your gender',
            },
            {
              name: 'age',
              type: 'number',
              label: 'Age',
              required: false,
              validation: {
                max: 120,
                min: 0,
              },
              description:
                'Senior citizens (age 65 or above) may qualify for a higher tax-free threshold.',
              placeholder: 'Enter your age as of the end of the tax year',
            },
            {
              name: 'is_disabled',
              type: 'select',
              label: 'Disability Status',
              options: ['Yes', 'No'],
              required: false,
              description:
                'Persons with disabilities may qualify for a higher tax-free threshold.',
              placeholder: 'Do you have a recognized disability?',
            },
            {
              name: 'is_freedom_fighter',
              type: 'select',
              label: 'Freedom Fighter Status',
              options: ['Yes', 'No'],
              required: false,
              description:
                'Gazetted freedom fighters / war-wounded individuals may qualify for an even higher tax-free threshold.',
              placeholder: 'Are you a Gazetted freedom fighter / war-wounded?',
            },
          ],
          category: 'Taxpayer Profile',
        },
        {
          index: 2,
          fields: [
            {
              name: 'salary_income',
              type: 'number',
              label: 'Salary Income',
              required: true,
              validation: { min: 0 },
              description:
                'Income from employment, subject to progressive tax rates.',
              placeholder: 'Enter your total annual salary income',
            },
            {
              name: 'business_professional_income',
              type: 'number',
              label: 'Business or Professional Income',
              required: false,
              validation: { min: 0 },
              description:
                'Income derived from business or professional activities.',
              placeholder:
                'Enter your total annual business or professional income',
            },
            {
              name: 'interest_on_securities',
              type: 'number',
              label: 'Interest on Securities',
              required: false,
              validation: { min: 0 },
              description:
                'Income received as interest from various securities.',
              placeholder: 'Enter total annual interest income from securities',
            },
            {
              name: 'house_property_income',
              type: 'number',
              label: 'Income from House Property',
              required: false,
              validation: { min: 0 },
              description: 'Income derived from renting out house property.',
              placeholder:
                'Enter total annual income from house property (e.g., rent)',
            },
            {
              name: 'capital_gains',
              type: 'number',
              label: 'Capital Gains',
              required: false,
              validation: { min: 0 },
              description:
                'Gains from the sale of capital assets, taxable in certain circumstances.',
              placeholder: 'Enter total annual capital gains (if applicable)',
            },
            {
              name: 'other_income',
              type: 'number',
              label: 'Other Income (including Deemed Income)',
              required: false,
              validation: { min: 0 },
              description:
                "Any other income not falling under specific heads, including 'deemed income' like unexplained investments.",
              placeholder: 'Enter any other taxable income not listed above',
            },
          ],
          category: 'Income Sources',
        },
        {
          index: 3,
          fields: [
            {
              name: 'total_eligible_investments',
              type: 'number',
              label: 'Total Eligible Investments for Rebate',
              required: false,
              validation: { min: 0 },
              description:
                'Investments that qualify for an investment tax rebate (e.g., 15% of eligible investment, subject to limits).',
              placeholder:
                'Enter the total amount of your eligible investments',
            },
          ],
          category: 'Tax-Saving Investments',
        },
        {
          index: 4,
          fields: [
            {
              name: 'net_wealth',
              type: 'number',
              label: 'Net Wealth',
              required: false,
              validation: { min: 0 },
              description:
                'If net worth exceeds BDT 40 million, a surcharge on net wealth may apply.',
              placeholder: 'Enter your total net wealth in BDT',
            },
            {
              name: 'car_engine_capacity_cc',
              type: 'number',
              label: 'Car Engine Capacity (in CC)',
              required: false,
              validation: { min: 0 },
              description:
                'An environmental/vehicle surcharge or advance tax may apply based on engine capacity for car owners.',
              placeholder:
                'Enter the engine capacity of your car(s) in cubic centimeters',
            },
          ],
          category: 'Surcharges & Additional Information',
        },
      ],
      statusCode: '200',
    },
    {
      title: 'Create Tax Calculation',
      method: 'POST',
      path: '/api/v1/tax/tax_calculation',
      description: '9. Create Tax Calculation',
      requestExample: {
        country: 'bangladesh',
        year: 2024,
        tax_form_data: [
          {
            index: 1,
            category: 'Taxpayer Profile',
            fields: [
              {
                name: 'assessment_year',
                value: '2025-26 (Income Year 2024-25) or later proposed',
              },
              {
                name: 'residency_status',
                value: 'Resident',
              },
              {
                name: 'gender',
                value: 'Male',
              },
              {
                name: 'age',
                value: 32,
              },
              {
                name: 'is_disabled',
                value: 'No',
              },
              {
                name: 'is_freedom_fighter',
                value: 'No',
              },
            ],
          },
          {
            index: 2,
            category: 'Income Sources',
            fields: [
              {
                name: 'salary_income',
                value: 900000,
              },
              {
                name: 'business_professional_income',
                value: 50000,
              },
              {
                name: 'interest_on_securities',
                value: 0,
              },
              {
                name: 'house_property_income',
                value: 0,
              },
              {
                name: 'capital_gains',
                value: 0,
              },
              {
                name: 'other_income',
                value: 0,
              },
            ],
          },
          {
            index: 3,
            category: 'Tax-Saving Investments',
            fields: [
              {
                name: 'total_eligible_investments',
                value: 120000,
              },
            ],
          },
          {
            index: 4,
            category: 'Surcharges & Additional Information',
            fields: [
              {
                name: 'net_wealth',
                value: 0,
              },
              {
                name: 'car_engine_capacity_cc',
                value: 0,
              },
            ],
          },
        ],
      },
      responseExample: {
        success: true,
        message: 'Tax calculation created successfully',
        tax_id: 7,
        processing_status: 'pending',
      },
      statusCode: '200',
    },
    {
      title: 'Update Tax Calculation',
      method: 'PUT',
      path: '/api/v1/tax/tax_calculation/{tax_id}',
      description: '10. Update Tax Calculation',
      requestExample: {
        tax_form_data: [
          {
            index: 2,
            category: 'Income Sources',
            fields: [
              {
                name: 'salary_income',
                value: 950000,
              },
              {
                name: 'business_professional_income',
                value: 60000,
              },
            ],
          },
        ],
      },
      responseExample: {
        success: true,
        message: 'Tax calculation updated successfully',
        tax_id: 3,
        processing_status: 'pending',
      },
      statusCode: '200',
    },
    {
      title: 'Delete Tax Calculation',
      method: 'DELETE',
      path: '/api/v1/tax/tax_calculation/{tax_id}',
      description: '11. Delete Tax Calculation',
      responseExample: {
        success: true,
        message: 'Tax calculation 7 deleted successfully',
        tax_id: 7,
      },
      statusCode: '200',
    },
    {
      title: 'Retry Tax Calculation',
      method: 'POST',
      path: '/api/v1/tax/tax_calculation/retry',
      description: '12. Retry Tax Calculation',
      requestExample: {
        tax_id: 3,
      },
      responseExample: {
        success: true,
        message: 'Tax calculation retry initiated successfully',
        tax_id: 3,
        processing_status: 'pending',
      },
      statusCode: '200',
    },
    {
      title: 'Get Tax Calculation by ID',
      method: 'GET',
      path: '/api/v1/tax/tax_calculation/{tax_id}',
      description: '13. Get Tax Calculation by ID',
      responseExample: {
        tax_id: 2,
        country: 'bangladesh',
        year: 2024,
        tax_form_data: [
          {
            index: 1,
            fields: [
              {
                name: 'assessment_year',
                value: '2025-26 (Income Year 2024-25) or later proposed',
              },
              {
                name: 'residency_status',
                value: 'Resident',
              },
              {
                name: 'gender',
                value: 'Male',
              },
              {
                name: 'age',
                value: 32,
              },
              {
                name: 'is_disabled',
                value: 'No',
              },
              {
                name: 'is_freedom_fighter',
                value: 'No',
              },
            ],
            category: 'Taxpayer Profile',
          },
          {
            index: 2,
            fields: [
              {
                name: 'salary_income',
                value: 900000,
              },
              {
                name: 'business_professional_income',
                value: 50000,
              },
              {
                name: 'interest_on_securities',
                value: 0,
              },
              {
                name: 'house_property_income',
                value: 0,
              },
              {
                name: 'capital_gains',
                value: 0,
              },
              {
                name: 'other_income',
                value: 0,
              },
            ],
            category: 'Income Sources',
          },
          {
            index: 3,
            fields: [
              {
                name: 'total_eligible_investments',
                value: 120000,
              },
            ],
            category: 'Tax-Saving Investments',
          },
          {
            index: 4,
            fields: [
              {
                name: 'net_wealth',
                value: 0,
              },
              {
                name: 'car_engine_capacity_cc',
                value: 0,
              },
            ],
            category: 'Surcharges & Additional Information',
          },
        ],
        processing_status: 'completed',
        error_message: null,
        total_income: [
          {
            type: 'salary_income',
            amount: 900000.0,
          },
          {
            type: 'business_professional_income',
            amount: 50000.0,
          },
        ],
        deductions: [
          {
            type: 'donation',
            claimed_amount: 10000,
            allowed_amount: 10000,
            limit: 20000,
          },
        ],
        tax_slabs: [
          {
            rate: '0%',
            range: '0 - 375,000',
            tax_amount: 0.0,
            applicable_amount: 375000.0,
          },
          {
            rate: '10%',
            range: '375,001 - 675,000',
            tax_amount: 30000.0,
            applicable_amount: 300000.0,
          },
          {
            rate: '15%',
            range: '675,001 - 1,075,000',
            tax_amount: 39750.0,
            applicable_amount: 265000.0,
          },
        ],
        surcharges: [
          {
            type: 'wealth_tax',
            description: '1% of net wealth',
            base_amount: 1000000,
            rate: '1%',
            threshold: 10000000,
            surcharge_amount: 0,
          },
        ],
        rebates: [
          {
            rate: '15%',
            type: 'Investment Tax Rebate',
            limit: 28500.0,
            rebate_amount: 18000.0,
            investment_value: 120000.0,
            applicable_amount: 120000.0,
          },
        ],
        calculation_summary: {
          final_tax: 51750.0,
          total_rebates: 18000.0,
          total_slab_tax: 69750.0,
          total_deductions: 10000.0,
          total_surcharges: 0.0,
          gross_total_income: 950000.0,
          net_taxable_income: 940000.0,
        },
        recommendations: [
          'Consider increasing your eligible investments to BDT 190,000 to maximize your investment tax rebate. An additional investment of BDT 70,000 (190,000 - 120,000) could increase your rebate by BDT 10,500 (from BDT 18,000 to BDT 28,500), reducing your final tax liability.',
          'Explore other tax-saving investment instruments (e.g., provident fund contributions, approved superannuation funds, life insurance premiums, government securities) to further optimize your tax planning, subject to the overall investment allowance limits.',
        ],
      },
      statusCode: '200',
    },
    {
      title: 'List All Tax Calculations',
      method: 'GET',
      path: '/api/v1/tax/tax_calculations',
      description: '14. List All Tax Calculations',
      responseExample: [
        {
          tax_id: 2,
          country: 'bangladesh',
          year: 2024,
          tax_form_data: [
            {
              index: 1,
              fields: [
                {
                  name: 'assessment_year',
                  value: '2025-26 (Income Year 2024-25) or later proposed',
                },
                {
                  name: 'residency_status',
                  value: 'Resident',
                },
                {
                  name: 'gender',
                  value: 'Male',
                },
                {
                  name: 'age',
                  value: 32,
                },
                {
                  name: 'is_disabled',
                  value: 'No',
                },
                {
                  name: 'is_freedom_fighter',
                  value: 'No',
                },
              ],
              category: 'Taxpayer Profile',
            },
            {
              index: 2,
              fields: [
                {
                  name: 'salary_income',
                  value: 900000,
                },
                {
                  name: 'business_professional_income',
                  value: 50000,
                },
                {
                  name: 'interest_on_securities',
                  value: 0,
                },
                {
                  name: 'house_property_income',
                  value: 0,
                },
                {
                  name: 'capital_gains',
                  value: 0,
                },
                {
                  name: 'other_income',
                  value: 0,
                },
              ],
              category: 'Income Sources',
            },
            {
              index: 3,
              fields: [
                {
                  name: 'total_eligible_investments',
                  value: 120000,
                },
              ],
              category: 'Tax-Saving Investments',
            },
            {
              index: 4,
              fields: [
                {
                  name: 'net_wealth',
                  value: 0,
                },
                {
                  name: 'car_engine_capacity_cc',
                  value: 0,
                },
              ],
              category: 'Surcharges & Additional Information',
            },
          ],
          processing_status: 'completed',
          error_message: null,
          total_income: [
            {
              type: 'salary_income',
              amount: 900000.0,
            },
            {
              type: 'business_professional_income',
              amount: 50000.0,
            },
          ],
          deductions: [
            {
              type: 'donation',
              claimed_amount: 10000,
              allowed_amount: 10000,
              limit: 20000,
            },
          ],
          tax_slabs: [
            {
              rate: '0%',
              range: '0 - 375,000',
              tax_amount: 0.0,
              applicable_amount: 375000.0,
            },
            {
              rate: '10%',
              range: '375,001 - 675,000',
              tax_amount: 30000.0,
              applicable_amount: 300000.0,
            },
            {
              rate: '15%',
              range: '675,001 - 1,075,000',
              tax_amount: 39750.0,
              applicable_amount: 265000.0,
            },
          ],
          surcharges: [
            {
              type: 'wealth_tax',
              description: '1% of net wealth',
              base_amount: 1000000,
              rate: '1%',
              threshold: 10000000,
              surcharge_amount: 0,
            },
          ],
          rebates: [
            {
              rate: '15%',
              type: 'Investment Tax Rebate',
              limit: 28500.0,
              rebate_amount: 18000.0,
              investment_value: 120000.0,
              applicable_amount: 120000.0,
            },
          ],
          calculation_summary: {
            final_tax: 51750.0,
            total_rebates: 18000.0,
            total_slab_tax: 69750.0,
            total_deductions: 10000.0,
            total_surcharges: 0.0,
            gross_total_income: 950000.0,
            net_taxable_income: 940000.0,
          },
          recommendations: [
            'Consider increasing your eligible investments to BDT 190,000 to maximize your investment tax rebate. An additional investment of BDT 70,000 (190,000 - 120,000) could increase your rebate by BDT 10,500 (from BDT 18,000 to BDT 28,500), reducing your final tax liability.',
            'Explore other tax-saving investment instruments (e.g., provident fund contributions, approved superannuation funds, life insurance premiums, government securities) to further optimize your tax planning, subject to the overall investment allowance limits.',
          ],
        },
      ],
      statusCode: '200',
    },
  ],
  useCases: [
    {
      title: 'Income & Deduction Analysis',
      description:
        'Calculate taxes based on salary, business income, capital gains, other income, and deductible investments.',
    },
    {
      title: 'Tax Calculation & Recommendations',
      description:
        'Compute taxes slab, rebates, surcharges, and provide personalized optimization recommendations based on income and investments.',
    },
    {
      title: 'Integration for SaaS Products',
      description:
        'Use the API for tax calculation in other SaaS/Fintech systems.',
    },
  ],
  targetUsers: [
    'Accountants & Tax Consultants: Quickly and accurately calculate clients’ taxes.',
    'Small & Medium Businesses: Automated tax reporting and form generation.',
    'Freelancers & Self-Employed Professionals: Easily track personal income and tax obligations.',
    'Enterprise HR & Finance Departments: Manage employee tax data efficiently.',
    'Fintech & Tax Software Platforms: Provide tax solutions through API integration in other software or applications.',
  ],
};
